import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { hasPermission } from "../utils/permissions";
import { FEATURE_KEYS, hasFeature, FeatureKey } from "../utils/featureAccess";

interface SidebarItemProps {
    text: string;
    path: string;
    isActive?: boolean;
    onClick?: () => void;
}

interface SidebarProps {
    onNavigate?: () => void;
}

type SidebarItemConfig = {
    text: string;
    path: string;
    feature?: FeatureKey;
    permission?: string;
    permissions?: string[];
};

const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
    const logoSrc = '/gymlogo.png';
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const items: SidebarItemConfig[] = [
        { text: "Dashboard", path: "/", permission: "dashboard.view" },
        { text: "Members", path: "/members", feature: FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS, permission: "members.view" },
        { text: "Employee", path: "/employee", feature: FEATURE_KEYS.PRIVATE_TRAINING, permission: "employees.view" },
        { text: "User Management", path: "/users", permissions: ["users.view"] },
        { text: "Classes", path: "/classes", feature: FEATURE_KEYS.MOBILE_APP, permission: "classes.view" },
        { text: "Courts", path: "/courts", permission: "courts.view" },
        { text: "Bookings", path: "/bookings", permission: "bookings.view" },
        { text: "Packages", path: "/packages", feature: FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS, permission: "packages.view" },
        { text: "Package Analytics", path: "/package-analytics", feature: FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS, permission: "packages.view" },
        { text: "Subscriptions", path: "/subscriptions", feature: FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS, permission: "subscriptions.view" },
        { text: "PT Sessions", path: "/sessions", feature: FEATURE_KEYS.PRIVATE_TRAINING, permission: "sessions.view" },
        { text: "Salary", path: "/salary", feature: FEATURE_KEYS.PRIVATE_TRAINING, permission: "salary.view" },
        { text: "Stock", path: "/stock", feature: FEATURE_KEYS.STOCK_PRODUCTS, permission: "stock.view" },
        { text: "Sales", path: "/sales", feature: FEATURE_KEYS.STOCK_PRODUCTS, permission: "sales.view" },
        { text: "Sales Reports", path: "/salesReports", feature: FEATURE_KEYS.ANALYTICS, permission: "sales_reports.view" },
        { text: "Accounting", path: "/accounting", feature: FEATURE_KEYS.ACCOUNTING, permission: "accounting.view" },
        { text: "Settings", path: "/settings", permissions: ["settings.view"] },
    ];

    const hasSidebarPermission = (item: SidebarItemConfig) => {
        if (item.permissions) {
            return item.permissions.some((permission) => hasPermission(user, permission));
        }

        return !item.permission || hasPermission(user, item.permission);
    };

    const handleLogout = () => {
        onNavigate?.();
        logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="flex h-screen w-56 flex-col bg-black p-3 text-white">
            <div className="mb-3 flex h-20 items-center justify-center border-b border-white/10 pb-3">
                <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" />
            </div>

            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                {items
                    .filter((item) => hasFeature(item.feature))
                    .filter((item) => hasSidebarPermission(item))
                    .map((item) => (
                        <SidebarItem
                            key={item.path}
                            text={item.text}
                            path={item.path}
                            isActive={pathname === item.path}
                            onClick={onNavigate}
                        />
                    ))}
            </nav>

            <button
                type="button"
                onClick={handleLogout}
                className="mt-3 w-full rounded-md bg-[#ac120c] px-3 py-2 text-left text-sm font-medium transition-colors hover:bg-[#8f100b]"
            >
                Logout
            </button>
        </div>
    );
};

const SidebarItem: React.FC<SidebarItemProps> = ({ text, path, isActive, onClick }) => {
    return (
        <Link
            to={path}
            onClick={onClick}
            className={`flex items-center rounded-md px-3 py-2 transition-colors ${isActive ? "bg-white/15 text-white" : "text-gray-300 hover:bg-white/10 hover:text-white"}`}
        >
            <span className="text-base font-medium">{text}</span>
        </Link>
    );
};

export default Sidebar;
